import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Container } from '@mantine/core';
import Header from '../../components/util/header';
import Background from '../../components/util/background';
import { fetchPermissions, PermissionOption } from '../../../libraries/user';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import { LoginState } from '../../client-redux/login/reducer';
import {
  addDistribuition,
  deleteDistribuition,
  DistribuitionRequests,
  DistribuitionsResponse,
  EventsResponse,
  fetchDistributionInfo,
  fetchEventList,
  updateDistribuition
} from '../../../libraries/distribuition-list';
import { useNavigate, useParams } from 'react-router-dom';
import DistribuitionForm from './distribuition-form';
import { useStatusOperDialog } from '../../context/status-oper-dialog';
import { StatusType } from '../../../libraries/zps';
import './styles.scss';

export enum MODE {
  //READ = 'read',
  EDIT = 'edit',
  NEW = 'new'
}

const Distribuition = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const login = useSelector<AppState, LoginState>(state => state.login);
  const controller = useRef(new AbortController()).current;
  const { mode: modeParam, id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [, /* noResults */ setNoResults] = useState<boolean>(true);
  const [distribuition, setDistribuition] = useState<DistribuitionsResponse>();
  const [events, setEvents] = useState<EventsResponse>();

  const [selectedPermissions, setSelectedPermissions] = useState<PermissionOption[]>([]);

  const initialTab = 'permissions';
  const [activeTab, setActiveTab] = useState<string>(initialTab);

  const { handleApiError, showStatusOperDialog } = useStatusOperDialog();

  const mode = useMemo(() => {
    switch (modeParam) {
      case MODE.NEW:
      case MODE.EDIT:
        return modeParam as MODE;
      default:
        return MODE.EDIT;
    }
  }, [modeParam]);

  useEffect(() => {
    if (id) {
      getEventsList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id]);

  useEffect(() => {
    if (mode === MODE.NEW) {
      fetchPermissions().then(permissionsResponse => {
        const preparedPermissions = permissionsResponse.data.list.map((permission: PermissionOption) => ({
          ...permission,
          selected: false
        }));
        setSelectedPermissions(preparedPermissions);
      });
    } else {
      getDistribuitionInfo();
    }
    getEventsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getEventsList = () => {
    if (!login.token) return;

    if (loading) {
      controller.abort();
    }

    setLoading(true);

    fetchEventList(controller)
      .then(response => {
        if (response.statusOper.code === 0) {
          setEvents(response);
          setNoResults(false);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        }
      })
      .finally(() => setLoading(false));
  };

  const getDistribuitionInfo = () => {
    if (!login.token) return;

    if (loading) {
      controller.abort();
    }

    setLoading(true);

    fetchDistributionInfo(Number(id), controller)
      .then(async response => {
        if (response.statusOper.code === 0) {
          setDistribuition(response);
          const permissionsResponse = await fetchPermissions();
          const existingPermissions = response.list[0]?.permissions ?? [];
          const preparedPermissions = permissionsResponse.data.list.map((permission: PermissionOption) => {
            const isSelected = existingPermissions.some(
              (p: { appId: number; permissionCode: number }) =>
                p.appId === permission.appId && (p.permissionCode & permission.mask) !== 0
            );

            return {
              ...permission,
              selected: isSelected
            };
          });

          setSelectedPermissions(preparedPermissions);
          setNoResults(false);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        }
      })
      .finally(() => setLoading(false));
  };

  const handleSubmit = async (formData: any) => {
    try {
      const permissionsMap: { [appId: number]: number } = {};

      selectedPermissions.forEach(p => {
        if (p.selected) {
          if (!permissionsMap[p.appId]) {
            permissionsMap[p.appId] = 0;
          }
          permissionsMap[p.appId] |= p.mask;
        }
      });

      const reducedPermissions = Object.entries(permissionsMap).map(([appId, permissionCode]) => ({
        appId: Number(appId),
        permissionCode
      }));

      const requestPayload: DistribuitionRequests = {
        ...formData,
        permissions: reducedPermissions
      };

      const response =
        mode === MODE.NEW
          ? await addDistribuition(requestPayload, controller)
          : await updateDistribuition(requestPayload, controller);

      if (!response || response.statusOper?.code !== 0) {
        handleApiError(
          response,
          mode === MODE.NEW
            ? lang.ADD_DISTRIBUITION_ERROR || 'Failed to add distribution'
            : lang.EDIT_DISTRIBUITION_ERROR || 'Failed to update distribution'
        );
        return;
      }

      showStatusOperDialog({
        type: StatusType.INFO,
        message:
          mode === MODE.NEW
            ? lang.ADD_DISTRIBUITION_SUCCESS || 'Distribution successfully added'
            : lang.EDIT_DISTRIBUITION_SUCCESS || 'Distribution successfully updated',
        statusOper: response.statusOper
      });

      setTimeout(() => {
        navigate('/distributions');
      }, 800);
    } catch (err: any) {
      handleApiError(
        err,
        mode === MODE.NEW
          ? lang.ADD_DISTRIBUITION_ERROR || 'Failed to add distribution'
          : lang.EDIT_DISTRIBUITION_ERROR || 'Failed to update distribution'
      );
    }
  };

  const handleDelete = async () => {
    try {
      const response = await deleteDistribuition(distribuition?.list[0].id || '0');

      if (!response || response.statusOper?.code !== 0) {
        handleApiError(response, lang.DELETE_DISTRIBUITION_ERROR || 'Failed to delete distribution');
        return;
      }

      showStatusOperDialog({
        type: StatusType.INFO,
        message: lang.DELETE_DISTRIBUITION_SUCCESS || 'Distribution successfully deleted',
        statusOper: response.statusOper
      });

      setTimeout(() => {
        navigate('/distributions');
      }, 800);
    } catch (err: any) {
      handleApiError(err, lang.DELETE_DISTRIBUITION_ERROR || 'Failed to delete distribution');
    }
  };

  const toggleSelection = (permission: PermissionOption) => {
    setSelectedPermissions(prevPermissions =>
      prevPermissions.map(prevPermission =>
        prevPermission.appId === permission.appId && prevPermission.mask === permission.mask
          ? { ...prevPermission, selected: !prevPermission.selected }
          : prevPermission
      )
    );
  };

  const distribuitionData = distribuition?.list?.[0] ?? {
    emails: [],
    entid: 0,
    eventCode: 0,
    eventDescription: '',
    id: '',
    permissions: [],
    users: []
  };

  return (
    <Background>
      <Container fluid classNames={{ root: 'distribuition' }}>
        <Header pageTitle={mode === MODE.NEW ? `${lang.DISTRIBUITIONS_ADD_BUTTON}` : `${lang.GLOBAL_EVENT}: ${id}`} />
        <DistribuitionForm
          mode={mode}
          lang={lang}
          isNew={mode === MODE.NEW ? true : false}
          distribuition={distribuitionData}
          eventsList={events?.events ?? []}
          submitForm={handleSubmit}
          deleteDistribuition={handleDelete}
          permissionOptions={selectedPermissions}
          togglePermissions={toggleSelection}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </Container>
    </Background>
  );
};

export default Distribuition;
